import type {AnyQueryRoute} from '@github-ui/react-core/future/query-route'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {hashKey, type Query, type QueryClient, useQueryClient} from '@github-ui/react-query'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useCallback, useLayoutEffect, useRef} from 'react'

import {canonicalQueryKey, requestPathname} from '../helpers/saved-view-route-cache'
import {usePendingSavedViewSearch} from './use-saved-view-navigation'

/** Board searches are expensive aggregations, so at most this many go out before anyone picks. */
export const MAX_PREFETCHES_IN_FLIGHT = 2

/** Reads the search parameters a saved view route sends for a URL, the same way its `mainQuery` `queryDeps` does. */
export type SavedViewRequestSearchParams = (searchParams: URLSearchParams) => Record<string, string>

function isViewPrefetch(routeId: string, query: Query): boolean {
  return query.queryKey[1] === routeId && query.queryKey[3] === 'mainQuery' && query.meta?.['isPreload'] === true
}

/**
 * Returns a handler that starts the request for the saved view at `url` and returns whether it
 * sent one. It sends nothing when the response is cached or loading, or when
 * `MAX_PREFETCHES_IN_FLIGHT` prefetches for `route` are out.
 *
 * `route.preload` reads `url` the same way navigation does, so navigating to `url` joins the
 * request or uses its response.
 */
export function useSavedViewPrefetch<Route extends AnyQueryRoute>(
  route: Route,
  params: Parameters<Route['preload']>[0],
): (url: string) => boolean {
  const queryClient = useQueryClient()

  return useCallback(
    (url: string) => {
      const predicate = (query: Query) => isViewPrefetch(route.id, query)
      const inFlight = queryClient.isFetching({predicate})
      if (inFlight >= MAX_PREFETCHES_IN_FLIGHT) return false

      route.preload(params, new URL(url, ssrSafeLocation.origin).searchParams)
      return queryClient.isFetching({predicate}) > inFlight
    },
    [params, queryClient, route],
  )
}

/**
 * Removes the prefetched responses for the saved view that `current` is keyed for, including
 * requests still in flight. Keeps the response under `current`, the `promoted` one, and the one a
 * navigation to `pendingSearch` on the view is loading, since removing a query cancels the request
 * that navigation waits on.
 *
 * Keys for other URLs on the view keep the request pathname in `current`, so `requestSearchParams`
 * is the only part that differs by route.
 */
export function evictSavedViewPrefetches(
  queryClient: QueryClient,
  {
    routeId,
    requestSearchParams,
    current,
    promoted,
    pendingSearch,
  }: {
    routeId: string
    requestSearchParams: SavedViewRequestSearchParams
    current: RouteQueryKey
    promoted?: RouteQueryKey
    pendingSearch?: string
  },
) {
  const pathname = requestPathname(current)
  if (pathname === undefined) return

  const keep = [current]
  if (promoted) keep.push(promoted)
  if (pendingSearch !== undefined) {
    keep.push(canonicalQueryKey(current, pathname, requestSearchParams(new URLSearchParams(pendingSearch))))
  }
  const kept = new Set(keep.map(key => hashKey(key)))
  queryClient.removeQueries({
    predicate: query =>
      isViewPrefetch(routeId, query) &&
      requestPathname(query.queryKey) === pathname &&
      !kept.has(hashKey(query.queryKey)),
  })
}

/**
 * Returns a handler that removes the prefetched responses for the saved view on screen, keeping
 * the one on screen, `promoted`, and the one a pending navigation on the view is loading.
 *
 * Call it after the view is saved or edited. Every URL that leaves a setting to the saved view
 * now has a different response, and navigation uses a fresh prefetch without asking again.
 *
 * The handler reads the response on screen and the pending navigation when it is called. A
 * mutation can keep the handler from an earlier render, before a navigation started or finished.
 */
export function useEvictSavedViewPrefetches(
  route: AnyQueryRoute,
  requestSearchParams: SavedViewRequestSearchParams,
): (promoted?: RouteQueryKey) => void {
  const queryClient = useQueryClient()
  const current = useRouteQueryKey(route, 'mainQuery')
  const pendingSearch = usePendingSavedViewSearch()
  const latestRef = useRef({current, pendingSearch})
  useLayoutEffect(() => {
    latestRef.current = {current, pendingSearch}
  }, [current, pendingSearch])

  return useCallback(
    (promoted?: RouteQueryKey) => {
      const latest = latestRef.current
      if (!latest.current) return
      evictSavedViewPrefetches(queryClient, {
        routeId: route.id,
        requestSearchParams,
        current: latest.current,
        promoted,
        pendingSearch: latest.pendingSearch,
      })
    },
    [queryClient, requestSearchParams, route.id],
  )
}
